/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

"use client";

import { ColumnDef } from "@tanstack/react-table";
import { SpeedTestResult } from "@/types/types";
import {
  createSortableHeader,
  createRightAlignedSortableHeader,
} from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import { TimeFormatSettings, formatDateTimeWithSettings } from "@/utils/timeSettings";

// Helper function to format speed
const formatSpeed = (speed: number) => {
  if (speed >= 1000) {
    return `${(speed / 1000).toFixed(1)} Gbps`;
  }
  return `${speed.toFixed(0)} Mbps`;
};

// Helper function to get test type badge styles
const getTestTypeBadgeClass = (testType: string) => {
  switch (testType) {
    case "iperf3":
      return "bg-purple-500/10 text-purple-600 dark:text-purple-400";
    case "librespeed":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
    default:
      return "bg-emerald-200/50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";
  }
};

// Helper function to get test type display name
const getTestTypeDisplayName = (testType: string) => {
  switch (testType) {
    case "iperf3":
      return "iperf3";
    case "librespeed":
      return "LibreSpeed";
    default:
      return "Speedtest.net";
  }
};

export const getSpeedTestColumns = (
  settings?: TimeFormatSettings
): ColumnDef<SpeedTestResult>[] => [
  {
    accessorKey: "createdAt",
    header: createSortableHeader("Date"),
    cell: ({ row }) => {
      const date = new Date(row.getValue("createdAt"));
      return (
        <span className="text-gray-700 dark:text-gray-300">
          {formatDateTimeWithSettings(date, settings)}
        </span>
      );
    },
    enableHiding: false, // Always show date
  },
  {
    accessorKey: "serverName",
    header: "Server",
    cell: ({ row }) => (
      <span
        className="text-gray-700 dark:text-gray-300 truncate block max-w-[180px] font-medium"
        title={row.getValue("serverName")}
      >
        {row.getValue("serverName")}
      </span>
    ),
    enableHiding: false, // Always show server
  },
  {
    accessorKey: "testType",
    header: "Type",
    cell: ({ row }) => {
      const testType = row.getValue("testType") as string;
      return (
        <span
          className={cn(
            "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
            getTestTypeBadgeClass(testType)
          )}
        >
          {getTestTypeDisplayName(testType)}
        </span>
      );
    },
    meta: {
      displayName: "Test Type",
    },
  },
  {
    accessorKey: "latency",
    header: createRightAlignedSortableHeader("Latency"),
    cell: ({ row }) => {
      const latency = parseFloat(row.getValue("latency"));
      return (
        <div className="text-right text-amber-600 dark:text-amber-400 font-mono font-medium">
          {latency.toFixed(1)}ms
        </div>
      );
    },
  },
  {
    accessorKey: "jitter",
    header: createRightAlignedSortableHeader("Jitter"),
    cell: ({ row }) => {
      const jitter = row.getValue("jitter") as number | null;
      return (
        <div className="text-right text-purple-600 dark:text-purple-400 font-mono font-medium">
          {jitter !== null && jitter !== undefined ? `${jitter.toFixed(1)}ms` : "—"}
        </div>
      );
    },
  },
  {
    accessorKey: "downloadSpeed",
    header: createRightAlignedSortableHeader("Download"),
    cell: ({ row }) => {
      const speed = row.getValue("downloadSpeed") as number;
      return (
        <div className="text-right text-blue-600 dark:text-blue-400 font-mono font-medium">
          {formatSpeed(speed)}
        </div>
      );
    },
  },
  {
    accessorKey: "uploadSpeed",
    header: createRightAlignedSortableHeader("Upload"),
    cell: ({ row }) => {
      const speed = row.getValue("uploadSpeed") as number;
      return (
        <div className="text-right text-emerald-600 dark:text-emerald-400 font-mono font-medium">
          {formatSpeed(speed)}
        </div>
      );
    },
  },
];

// Mobile-friendly columns with fewer fields
export const getSpeedTestMobileColumns = (
  settings?: TimeFormatSettings
): ColumnDef<SpeedTestResult>[] => [
  {
    id: "summary",
    cell: ({ row }) => {
      const test = row.original;
      const testType = test.testType;

      return (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="text-gray-700 dark:text-gray-300 text-base font-medium truncate flex-1 mr-2">
              {test.serverName}
            </div>
            <span
              className={cn(
                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0",
                getTestTypeBadgeClass(testType)
              )}
            >
              {getTestTypeDisplayName(testType)}
            </span>
          </div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">
            {formatDateTimeWithSettings(test.createdAt, settings)}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Latency:</span>
              <span className="text-amber-600 dark:text-amber-400 font-mono font-semibold">
                {parseFloat(test.latency).toFixed(1)}ms
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Jitter:</span>
              <span className="text-purple-600 dark:text-purple-400 font-mono font-semibold">
                {test.jitter !== null && test.jitter !== undefined ? `${test.jitter.toFixed(1)}ms` : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                Download:
              </span>
              <span className="text-blue-600 dark:text-blue-400 font-mono font-semibold">
                {formatSpeed(test.downloadSpeed)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Upload:</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-mono font-semibold">
                {formatSpeed(test.uploadSpeed)}
              </span>
            </div>
          </div>
        </div>
      );
    },
  },
];
